import React, { useEffect, useMemo, useState } from 'react';
import { AdminSidebar } from '../AdminDashboardPage';
import AdminHeader from '../AdminHeader';
import { getAllOrders } from '../../../services/orderService';
import formatPrice from '../../../utils/formatPrice';
import { Pie, Bar, Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
} from 'chart.js';
import '../../../css/orderReport.css';
import {
  ShoppingBag,
  CheckCircle2,
  Loader,
  XCircle,
  TrendingUp,
  Receipt,
  Truck,
  CreditCard,
  FileSpreadsheet,
  Filter,
  Calendar,
  Search,
} from 'lucide-react';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title
);

const STATUS_TONE = {
  Placed: 'neutral',
  Processing: 'warn',
  Shipped: 'info',
  Delivered: 'success',
  Cancelled: 'danger',
};

// Same status palette as the dashboard's order-status doughnut
const STATUS_COLORS = {
  Placed: '#4a6382',
  Processing: '#c48a2c',
  Shipped: '#8a6a9e',
  'Out for Delivery': '#3d8796',
  Delivered: '#2e6a4f',
  Cancelled: '#b42318',
  Rejected: '#d9466f',
  Returned: '#8f8178',
};

// Match the dashboard's chart typography + tooltip
ChartJS.defaults.font.family = "'Figtree', 'Segoe UI', system-ui, sans-serif";
ChartJS.defaults.font.size = 12;
ChartJS.defaults.color = '#8f8178';
Object.assign(ChartJS.defaults.plugins.tooltip, {
  backgroundColor: '#211915',
  titleColor: '#f5ede6',
  bodyColor: '#f5ede6',
  padding: 10,
  cornerRadius: 8,
  displayColors: false,
});

const PALETTE = ['#f4b6c4', '#4a6382', '#2e6a4f', '#c48a2c', '#8a6a9e', '#3d8796', '#d9466f', '#8f8178'];
const GRID_COLOR = '#f0ebe6';
const AXIS_COLOR = '#e7dfd8';

const pieLegend = {
  position: 'bottom',
  labels: { usePointStyle: true, pointStyle: 'rectRounded', boxWidth: 8, boxHeight: 8, padding: 16, color: '#4a3d36' },
};

const toISODate = (d) => d.toISOString().slice(0, 10);

const PRESETS = [
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: 'month', label: 'This month' },
  { key: 'all', label: 'All time' },
  { key: 'custom', label: 'Custom' },
];

const rangeForPreset = (preset) => {
  const today = new Date();
  const to = toISODate(today);
  if (preset === '7d') {
    const from = new Date(today); from.setDate(from.getDate() - 6);
    return { from: toISODate(from), to };
  }
  if (preset === '30d') {
    const from = new Date(today); from.setDate(from.getDate() - 29);
    return { from: toISODate(from), to };
  }
  if (preset === 'month') {
    const from = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: toISODate(from), to };
  }
  if (preset === 'all') {
    return { from: '2000-01-01', to };
  }
  return { from: to, to };
};

const daysBetween = (a, b) => (new Date(b) - new Date(a)) / (1000 * 60 * 60 * 24);

const formatDate = (v) => v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const OrderReportPage = () => {
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [preset, setPreset] = useState('30d');
  const [range, setRange] = useState(rangeForPreset('30d'));
  const [search, setSearch] = useState('');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const list = await getAllOrders({});
        setAllOrders(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error('Error loading orders for report:', err);
        setError('Could not load orders.');
        setAllOrders([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const applyPreset = (key) => {
    setPreset(key);
    if (key !== 'custom') setRange(rangeForPreset(key));
  };

  const handleCustomDate = (field, value) => {
    setPreset('custom');
    setRange((prev) => ({ ...prev, [field]: value }));
  };

  // ---- filter by date range ----
  const filtered = useMemo(() => {
    const fromTime = new Date(range.from).setHours(0, 0, 0, 0);
    const toTime = new Date(range.to).setHours(23, 59, 59, 999);
    return allOrders.filter((o) => {
      const t = new Date(o.createdAt).getTime();
      return t >= fromTime && t <= toTime;
    });
  }, [allOrders, range]);

  // ---- searched (for the detail table only) ----
  const searched = useMemo(() => {
    if (!search.trim()) return filtered;
    const q = search.trim().toLowerCase();
    return filtered.filter((o) =>
      o.orderId?.toLowerCase().includes(q) ||
      o.user?.email?.toLowerCase().includes(q) ||
      o.user?.name?.toLowerCase().includes(q) ||
      o.shippingAddress?.fullName?.toLowerCase().includes(q)
    );
  }, [filtered, search]);

  // ---- aggregates ----
  const stats = useMemo(() => {
    const total = filtered.length;
    const delivered = filtered.filter((o) => o.isDelivered).length;
    const cancelled = filtered.filter((o) => o.status === 'Cancelled').length;
    const inProgress = total - delivered - cancelled;
    const paid = filtered.filter((o) => o.isPaid).length;
    const revenue = filtered.reduce((sum, o) => sum + (!['Cancelled', 'Rejected', 'Returned'].includes(o.status) ? o.totalPrice || 0 : 0), 0);
    const avgOrderValue = total ? revenue / (total - cancelled || 1) : 0;

    const deliveryDurations = filtered
      .filter((o) => o.isDelivered && o.deliveredAt)
      .map((o) => daysBetween(o.createdAt, o.deliveredAt));
    const avgDeliveryDays = deliveryDurations.length
      ? (deliveryDurations.reduce((a, b) => a + b, 0) / deliveryDurations.length)
      : 0;

    const statusMap = {};
    filtered.forEach((o) => { statusMap[o.status || 'Unknown'] = (statusMap[o.status || 'Unknown'] || 0) + 1; });

    const paymentMap = {};
    filtered.forEach((o) => {
      const key = o.paymentMethod || 'Unknown';
      if (!paymentMap[key]) paymentMap[key] = { count: 0, revenue: 0 };
      paymentMap[key].count += 1;
      paymentMap[key].revenue += o.totalPrice || 0;
    });

    const reasonMap = {};
    filtered.filter((o) => o.status === 'Cancelled' && o.cancelReason).forEach((o) => {
      reasonMap[o.cancelReason] = (reasonMap[o.cancelReason] || 0) + 1;
    });

    const trendMap = {};
    filtered.forEach((o) => {
      const day = new Date(o.createdAt).toISOString().slice(0, 10);
      if (!trendMap[day]) trendMap[day] = { orders: 0, revenue: 0 };
      trendMap[day].orders += 1;
      if (!['Cancelled', 'Rejected', 'Returned'].includes(o.status)) trendMap[day].revenue += o.totalPrice || 0;
    });
    const trend = Object.entries(trendMap)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, v]) => ({ date, ...v }));

    return {
      total, delivered, cancelled, inProgress, paid,
      revenue, avgOrderValue, avgDeliveryDays,
      statusBreakdown: Object.entries(statusMap).map(([status, count]) => ({ status, count })),
      paymentBreakdown: Object.entries(paymentMap).map(([method, v]) => ({ method, ...v })),
      cancellationReasons: Object.entries(reasonMap).map(([reason, count]) => ({ reason, count })).sort((a, b) => b.count - a.count),
      trend,
    };
  }, [filtered]);

  const trendData = {
    labels: stats.trend.map((p) => p.date),
    datasets: [{
      label: 'Orders',
      data: stats.trend.map((p) => p.orders),
      borderColor: '#e38aa2',
      borderWidth: 2,
      backgroundColor: 'rgba(176, 122, 82, 0.12)',
      pointBackgroundColor: '#e38aa2',
      pointBorderColor: '#ffffff',
      pointRadius: 3,
      pointHoverRadius: 5,
      tension: 0.35,
      fill: true,
    }],
  };

  const statusData = {
    labels: stats.statusBreakdown.map((s) => s.status),
    datasets: [{
      data: stats.statusBreakdown.map((s) => s.count),
      backgroundColor: stats.statusBreakdown.map((s, i) => STATUS_COLORS[s.status] || PALETTE[i % PALETTE.length]),
      borderColor: '#ffffff',
      borderWidth: 3,
      hoverOffset: 4,
    }],
  };

  const paymentData = {
    labels: stats.paymentBreakdown.map((p) => p.method),
    datasets: [{
      label: 'Orders',
      data: stats.paymentBreakdown.map((p) => p.count),
      backgroundColor: '#f4b6c4',
      hoverBackgroundColor: '#e38aa2',
      borderWidth: 0,
      borderRadius: 6,
      maxBarThickness: 40,
    }],
  };

  const countScales = {
    y: { beginAtZero: true, ticks: { precision: 0 }, grid: { color: GRID_COLOR }, border: { display: false } },
    x: { grid: { display: false }, border: { color: AXIS_COLOR } },
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: countScales,
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: countScales,
  };

  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: pieLegend },
  };

  const maxReason = Math.max(1, ...stats.cancellationReasons.map((r) => r.count));

  const handleExportCsv = () => {
    const rows = [
      ['Order ID', 'Customer', 'Email', 'Items', 'Total', 'Payment', 'Paid', 'Status', 'Placed', 'Delivered'],
      ...searched.map((o) => [
        o.orderId,
        o.user?.name || o.shippingAddress?.fullName || 'Guest',
        o.user?.email || '',
        o.orderItems?.reduce((sum, it) => sum + it.qty, 0) || 0,
        o.totalPrice,
        o.paymentMethod,
        o.isPaid ? 'Yes' : 'No',
        o.status,
        formatDate(o.createdAt),
        formatDate(o.deliveredAt),
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `order_report_${range.from}_to_${range.to}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const kpis = [
    { key: 'total', label: 'Total orders', value: stats.total, icon: ShoppingBag, note: 'Placed in selected period' },
    { key: 'delivered', label: 'Delivered', value: stats.delivered, icon: CheckCircle2, note: 'Completed deliveries' },
    { key: 'progress', label: 'In progress', value: stats.inProgress, icon: Loader, note: 'Not yet delivered' },
    {
      key: 'cancelled',
      label: 'Cancelled',
      value: stats.cancelled,
      icon: XCircle,
      tone: stats.cancelled > 0 ? 'danger' : null,
      note: 'Cancelled orders',
    },
    { key: 'revenue', label: 'Revenue', value: formatPrice(stats.revenue), icon: TrendingUp, note: 'Excludes cancelled & returned' },
    { key: 'aov', label: 'Avg. order value', value: formatPrice(stats.avgOrderValue), icon: Receipt, note: 'Per non-cancelled order' },
    { key: 'delivery', label: 'Avg. delivery time', value: `${stats.avgDeliveryDays.toFixed(1)}d`, icon: Truck, note: 'Order placed to delivered' },
    { key: 'paid', label: 'Paid orders', value: `${stats.paid}/${stats.total}`, icon: CreditCard, note: 'Payment received' },
  ];

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content">
        <AdminHeader title="Order Report" />

        <div className="orp-page">
          {/* Page heading */}
          <div className="orp-heading">
            <div>
              <h1 className="orp-title">Order Report</h1>
              <p className="orp-subtitle">Order volume, fulfilment, payment mix and cancellations</p>
            </div>
            <div className="orp-heading-actions">
              <button
                type="button"
                className="orp-btn orp-btn-primary"
                onClick={handleExportCsv}
                disabled={loading || !searched.length}
              >
                <FileSpreadsheet size={15} />
                <span>Export CSV</span>
              </button>
            </div>
          </div>

          {/* Filter bar */}
          <div className="orp-filter-bar">
            <div className="orp-filter-left">
              <span className="orp-filter-label">
                <Filter size={13} />
                <span>Period</span>
              </span>
              <div className="orp-presets" role="group" aria-label="Date filter presets">
                {PRESETS.map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    className={`orp-preset-btn ${preset === p.key ? 'active' : ''}`}
                    onClick={() => applyPreset(p.key)}
                    aria-pressed={preset === p.key}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="orp-date-inputs">
              <span className="orp-date-icon">
                <Calendar size={14} />
              </span>
              <input type="date" value={range.from} max={range.to} aria-label="From date" onChange={(e) => handleCustomDate('from', e.target.value)} />
              <span className="orp-date-sep">to</span>
              <input type="date" value={range.to} min={range.from} aria-label="To date" onChange={(e) => handleCustomDate('to', e.target.value)} />
            </div>
          </div>

          {error && <div className="orp-error">{error}</div>}

          {/* Summary cards */}
          <div className="orp-grid">
            {kpis.map(({ key, label, value, icon: Icon, tone, note }) => (
              <div key={key} className={`orp-kpi ${tone && !loading ? `orp-kpi--${tone}` : ''}`}>
                <div className="orp-kpi-top">
                  <span className="orp-kpi-label">{label}</span>
                  <span className="orp-kpi-icon">
                    <Icon size={16} />
                  </span>
                </div>
                <p className="orp-kpi-value">{loading ? '—' : value}</p>
                <span className="orp-kpi-note">{note}</span>
              </div>
            ))}
          </div>

          {/* Trend */}
          <section className="orp-panel">
            <header className="orp-panel-head">
              <div>
                <h3 className="orp-panel-title">Orders trend</h3>
                <p className="orp-panel-sub">Orders placed per day</p>
              </div>
              <span className="orp-tag">Daily</span>
            </header>
            {stats.trend.length === 0 && !loading ? (
              <p className="orp-empty">No orders in this range.</p>
            ) : (
              <div className="orp-chart">
                <Line data={trendData} options={lineOptions} />
              </div>
            )}
          </section>

          {/* Status + Payment */}
          <div className="orp-row">
            <section className="orp-panel">
              <header className="orp-panel-head">
                <div>
                  <h3 className="orp-panel-title">Status mix</h3>
                  <p className="orp-panel-sub">Orders by current lifecycle status</p>
                </div>
              </header>
              {stats.statusBreakdown.length === 0 && !loading ? (
                <p className="orp-empty">No orders in this range.</p>
              ) : (
                <div className="orp-chart orp-chart--pie">
                  <Pie data={statusData} options={pieOptions} />
                </div>
              )}
            </section>
            <section className="orp-panel">
              <header className="orp-panel-head">
                <div>
                  <h3 className="orp-panel-title">Payment methods</h3>
                  <p className="orp-panel-sub">Number of orders per payment method</p>
                </div>
              </header>
              {stats.paymentBreakdown.length === 0 && !loading ? (
                <p className="orp-empty">No payment data.</p>
              ) : (
                <div className="orp-chart">
                  <Bar data={paymentData} options={barOptions} />
                </div>
              )}
            </section>
          </div>

          {/* Cancellation reasons */}
          <section className="orp-panel">
            <header className="orp-panel-head">
              <div>
                <h3 className="orp-panel-title">Cancellation reasons</h3>
                <p className="orp-panel-sub">Why customers cancelled in this period</p>
              </div>
            </header>
            {stats.cancellationReasons.length === 0 && !loading ? (
              <p className="orp-empty">No cancellations in this range.</p>
            ) : (
              <div className="orp-reason-list">
                {stats.cancellationReasons.map((r) => (
                  <div className="orp-reason-row" key={r.reason}>
                    <span className="orp-reason-label" title={r.reason}>{r.reason}</span>
                    <div className="orp-reason-bar-track">
                      <div className="orp-reason-bar-fill" style={{ width: `${(r.count / maxReason) * 100}%` }} />
                    </div>
                    <span className="orp-reason-count">{r.count}</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Full order detail table */}
          <section className="orp-panel">
            <header className="orp-panel-head">
              <div>
                <h3 className="orp-panel-title">All orders in range</h3>
                <p className="orp-panel-sub">{searched.length} {searched.length === 1 ? 'order' : 'orders'}</p>
              </div>
              <div className="orp-panel-actions">
                <input
                  type="text"
                  placeholder="Search order ID, name, email…"
                  className="orp-search-input"
                  aria-label="Search orders"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </div>
            </header>

            {searched.length === 0 && !loading ? (
              <p className="orp-empty">
                <Search size={16} />
                No orders match.
              </p>
            ) : (
              <div className="orp-table-wrap orp-table-wrap--scroll">
                <table className="orp-table">
                  <thead>
                    <tr>
                      <th>Order</th>
                      <th>Customer</th>
                      <th className="orp-num">Items</th>
                      <th className="orp-num">Total</th>
                      <th>Payment</th>
                      <th>Status</th>
                      <th>Placed</th>
                      <th>Delivered</th>
                    </tr>
                  </thead>
                  <tbody>
                    {searched.map((o) => {
                      const tone = STATUS_TONE[o.status] || 'neutral';
                      const qty = o.orderItems?.reduce((sum, it) => sum + it.qty, 0) || 0;
                      return (
                        <tr key={o._id}>
                          <td className="orp-mono">{o.orderId}</td>
                          <td>
                            <div className="orp-customer-cell">
                              <span>{o.user?.name || o.shippingAddress?.fullName || 'Guest'}</span>
                              <span className="orp-muted">{o.user?.email || o.shippingAddress?.phone || '—'}</span>
                            </div>
                          </td>
                          <td className="orp-num">{qty}</td>
                          <td className="orp-num orp-strong">{formatPrice(o.totalPrice)}</td>
                          <td>
                            {o.paymentMethod} · <span className={o.isPaid ? 'orp-paid-yes' : 'orp-paid-no'}>{o.isPaid ? 'Paid' : 'Unpaid'}</span>
                          </td>
                          <td><span className={`orp-pill orp-pill-${tone}`}>{o.status}</span></td>
                          <td className="orp-muted">{formatDate(o.createdAt)}</td>
                          <td className="orp-muted">{formatDate(o.deliveredAt)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default OrderReportPage;
